import { useMemo, useState } from 'react';
import { useConnection } from 'wagmi';
import { stakingManagerAbi } from '@syntra/abi';
import { ActionButton } from '../components/tx/ActionButton.tsx';
import { CountUp } from '../components/ui/motion.tsx';
import { AmountInput, Card, Countdown, Pill, Rows, SectionHead, Segmented } from '../components/ui/primitives.tsx';
import { AreaChart, type XY } from '../components/ui/viz.tsx';
import { DEPLOYMENT as D } from '../config/env.ts';
import { useChainNow } from '../hooks/useChainClock.tsx';
import { usePosition, useStakes, type StakeLot } from '../hooks/usePosition.ts';
import { NO_LOCK, PERMANENT, PLAN_IDS, useProtocol, type PlanId, type PlanView, type Protocol } from '../hooks/useProtocol.ts';
import { useI18n, type MessageKey } from '../i18n/index.tsx';
import { fmtBps, fmtNum, fmtPct, fmtToken, fmtUsd, parseAmount, toInput, toNum, fmtBalance } from '../lib/format.ts';
import { afterExitFee, aprOf, apyOf, planMultiple, stakePreview } from '../lib/plans.ts';
import { Coin, CoinStack, coinCount } from '../components/ui/coins.tsx';
import { JoinFirst } from '../components/tx/JoinFirst.tsx';
import { PriceMoving } from '../components/ui/PriceMoving.tsx';

const MAX = 2n ** 256n - 1n;

export const planName = (id: number) => `plan.${id}` as MessageKey;
const lockEpochs = (pl: PlanView) => (pl.permanent || pl.lockDuration === 0 ? null : Math.round(pl.lockDuration / 43_200));

export function Stake() {
  const { t } = useI18n();
  const { data: p } = useProtocol();
  const [planId, setPlanId] = useState<PlanId>(PERMANENT);
  const plan = p.plans?.[planId];
  return (
    <>
      <SectionHead eyebrow={t('stake.eyebrow')} title={t('stake.title')} />
      <PriceMoving />
      <PlanPicker plans={p.plans} selected={planId} onSelect={setPlanId} />
      <div className="grid two stake-top">
        <StakeForm plan={plan} p={p} />
        <Projection plan={plan} noLock={p.plans?.[NO_LOCK]} />
      </div>
      <Positions p={p} />
      <p className="muted" style={{ fontSize: 12, margin: 0 }}>
        {t('common.notAdvice')}
      </p>
    </>
  );
}

/**
 * The six plans side by side: APR (the client's figure, "up to"), the compounded APY, the lock and the fees. Once the
 * reward budget binds, each card also says what the plan pays now, and its APY follows that.
 */
export function PlanPicker({ plans, selected, onSelect }: { plans?: PlanView[]; selected: PlanId; onSelect: (id: PlanId) => void }) {
  const { t } = useI18n();
  // each plan's coin stack is as tall as its APR next to the best plan's
  const best = Math.max(...PLAN_IDS.map((id) => (plans?.[id] ? aprOf(toNum(plans[id].rate)) : 0)));
  return (
    <div className="plan-grid" role="radiogroup" aria-label={t('stake.choose')}>
      {PLAN_IDS.map((id) => {
        const pl = plans?.[id];
        const r = pl ? toNum(pl.rate) : NaN;
        const capped = Boolean(pl && pl.paidRate < pl.rate);
        const paid = pl ? toNum(pl.paidRate) : NaN;
        return (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={selected === id}
            className={`plan-card${selected === id ? ' active' : ''}${id === PERMANENT ? ' permanent' : ''}`}
            onClick={() => onSelect(id)}
            data-testid={`plan-${id}`}
            data-lean
          >
            {id === PERMANENT ? (
              // the plan the operators promote: its own colour and a ribbon; "zero fees" only while it charges none
              <span className="plan-ribbon" data-testid="plan-featured">
                {pl && pl.stakeFeeBps === 0n && pl.unstakeFeeBps === 0n ? t('plan.featuredNoFees') : t('plan.featured')}
              </span>
            ) : null}
            {pl ? <CoinStack token="SYN" count={coinCount(aprOf(r), best, 7)} size={22} label={`${fmtPct(aprOf(r), 0)} APR`} /> : null}
            <span className="plan-name">{t(planName(id))}</span>
            <span className="plan-apr num">
              <small>{t('plan.upTo')}</small> {pl ? fmtPct(aprOf(r), 0) : '—'} <small>APR</small>
            </span>
            {capped ? (
              <span className="plan-paying num" data-testid={`plan-${id}-paying`}>
                {t('plan.paying', { pct: fmtPct(aprOf(paid), 0) })}
              </span>
            ) : null}
            <span className="plan-apy num">{pl ? t('plan.apy', { pct: fmtPct(apyOf(capped ? paid : r), 1) }) : ' '}</span>
            <span className="plan-lock">
              {!pl ? ' ' : pl.permanent ? t('plan.lockForever') : pl.lockDuration === 0 ? t('plan.lockNone') : t('plan.lockDays', { days: Math.round(pl.lockDuration / 86_400) })}
            </span>
            <span className="plan-fees muted">
              {!pl ? ' ' : pl.stakeFeeBps === 0n && pl.unstakeFeeBps === 0n ? t('plan.noFees') : t('plan.fees', { in: fmtBps(pl.stakeFeeBps), out: fmtBps(pl.unstakeFeeBps) })}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function StakeForm({ plan, p }: { plan?: PlanView; p: Protocol }) {
  const { t } = useI18n();
  const { address } = useConnection();
  const { data: me } = usePosition(address);
  const [input, setInput] = useState('');
  const amount = parseAmount(input);
  // the contract books the lower of the average and the spot price (audit M-1)
  const booking = p.price !== undefined && p.spot !== undefined && p.spot < p.price ? p.spot : p.price;
  const preview = stakePreview(amount ?? 0n, plan?.stakeFeeBps ?? 0n, booking);
  const now = useChainNow(60_000);
  const blocked = !plan
    ? t('common.loading')
    : amount === null || amount === 0n
      ? t('common.enterAmount')
      : me.syntra !== undefined && amount > me.syntra
        ? t('common.insufficient', { token: 'SYN' })
        : null;
  const id = plan?.id ?? NO_LOCK;
  const unlock =
    !plan || plan.lockDuration === 0 || plan.permanent ? null : new Date((now + plan.lockDuration) * 1000).toLocaleDateString();
  return (
    <Card title={t('stake.form', { plan: t(planName(id)) })} highlight>
      <p className="soft" style={{ marginTop: 0 }}>
        {t(id === NO_LOCK ? 'plan.about0' : id === PERMANENT ? 'plan.aboutPermanent' : 'plan.aboutLocked', {
          days: plan ? Math.round(plan.lockDuration / 86_400) : 0,
        })}
      </p>
      <div className="field" style={{ gap: 14 }}>
        <AmountInput
          id="stake-amount"
          label={t('stake.amount')}
          value={input}
          onChange={setInput}
          token="SYN"
          icon="/token-syntra.png"
          balance={fmtBalance(me.syntra)}
          onMax={me.syntra !== undefined ? () => setInput(toInput(me.syntra!)) : undefined}
        />
        <Rows
          items={[
            plan?.stakeFeeBps === 0n ? [t('stake.feeNone'), t('stake.noFee')] : [t('stake.fee', { pct: plan ? fmtBps(plan.stakeFeeBps) : '—' }), `${fmtToken(preview.fee)} SYN`],
            [t('stake.youGet'), `${fmtToken(preview.net)} sSYN`],
            [t('stake.principal'), fmtUsd(preview.usd)],
            [t('stake.unlocks'), plan?.permanent ? t('plan.never') : unlock ?? t('plan.anytime')],
            [
              t('stake.exitFee'),
              !plan
                ? '—'
                : plan.unstakeFeeBps === 0n
                  ? t(plan.permanent ? 'stake.exitFeePermanentFree' : 'stake.noFee')
                  : t(plan.permanent ? 'stake.exitFeePermanent' : 'stake.exitFeeNote', { pct: fmtBps(plan.unstakeFeeBps) }),
            ],
          ]}
        />
        <JoinFirst>
          <ActionButton
            label={t('stake.cta', { plan: t(planName(id)) })}
            blocked={blocked}
            approve={amount ? { token: D.syntra, spender: D.staking, amount, symbol: 'SYN' } : undefined}
            request={amount && plan ? { address: D.staking, abi: stakingManagerAbi, functionName: 'stake', args: [id, amount] } : null}
            onDone={() => setInput('')}
          />
        </JoinFirst>
      </div>
    </Card>
  );
}

/** Every stake as a tile across the full width: plan and status, value and when it can leave, one action. */
function Positions({ p }: { p: Protocol }) {
  const { t } = useI18n();
  const { address } = useConnection();
  const { lots } = useStakes(address);
  const now = useChainNow(5000);
  const shown = lots.filter((l) => !l.empty || l.origin !== NO_LOCK);
  return (
    <Card title={t('stake.positions')}>
      {shown.length === 0 ? (
        <p className="soft">{address ? t('stake.noLots') : t('common.connectFirst')}</p>
      ) : (
        <ul className="lot-list">
          {shown.map((l) => (
            <LotRow key={l.id} lot={l} now={now} fee={p.plans?.[l.origin]?.unstakeFeeBps} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function LotRow({ lot: l, now, fee }: { lot: StakeLot; now: number; fee?: bigint }) {
  const { t } = useI18n();
  const permanent = l.origin === PERMANENT;
  const timed = l.origin !== NO_LOCK && !permanent;
  const locked = !l.empty && timed && l.unlockAt > now;
  const ended = !l.empty && timed && !locked;
  // a badge only where it adds something: the plan name already says "No lock" or "Permanent"
  const pill = l.empty
    ? { tone: 'muted' as const, text: t('stake.withdrawn') }
    : permanent
      ? null
      : locked
        ? { tone: 'amber' as const, text: t('stake.lockedUntil') }
        : ended
          ? { tone: 'green' as const, text: t('stake.lockEnded') }
          : null;
  const out = l.withdrawable; // for the permanent plan: the rewards above the deposit
  return (
    <li className="lot-row" data-testid={`lot-${l.id}`}>
      <div className="lot-main">
        <div className="lot-head">
          <b>{t(planName(l.origin))}</b>
          {pill ? <Pill tone={pill.tone}>{pill.text}</Pill> : null}
        </div>
        <div className="lot-value num">{l.empty ? '—' : `${fmtToken(l.value)} sSYN`}</div>
        <div className="lot-when muted">
          {l.empty ? null : permanent ? (
            t('stake.permanentNote')
          ) : l.origin === NO_LOCK ? (
            t('plan.lockNone')
          ) : locked ? (
            <>
              {t('stake.unlocksIn')} <Countdown to={l.unlockAt} />
            </>
          ) : (
            <>
              {t('stake.endedOn', { date: new Date(l.unlockAt * 1000).toLocaleDateString() })} · {t('stake.endedRate')}
            </>
          )}
        </div>
      </div>
      <div className="lot-action">
        {permanent && !l.empty ? (
          <ActionButton
            variant="secondary"
            label={t('stake.claimRewards')}
            blocked={l.withdrawable === 0n ? t('stake.noRewardsYet') : null}
            request={{ address: D.staking, abi: stakingManagerAbi, functionName: 'withdraw', args: [BigInt(l.id), MAX] }}
          />
        ) : !l.empty && l.withdrawable > 0n ? (
          <ActionButton
            variant="secondary"
            label={t('stake.withdraw')}
            request={{ address: D.staking, abi: stakingManagerAbi, functionName: 'withdraw', args: [BigInt(l.id), MAX] }}
          />
        ) : null}
        {!l.empty && fee !== undefined && out > 0n ? (
          <div className="muted lot-net">
            {fee === 0n
              ? t('stake.youReceiveNoFee', { amount: fmtToken(out) })
              : t('stake.youReceiveNet', { amount: fmtToken(afterExitFee(out, fee)), pct: fmtBps(fee) })}
          </div>
        ) : null}
      </div>
    </li>
  );
}

/** What 1,000 SYN becomes in the chosen plan at today's rates (a timed lock switches to the no-lock rate). */
function Projection({ plan, noLock }: { plan?: PlanView; noLock?: PlanView }) {
  const { t } = useI18n();
  const [period, setPeriod] = useState<'30' | '90' | '365'>('365');
  const r = plan ? toNum(plan.paidRate) : NaN; // what the plan pays today, not its ceiling
  const r0 = noLock ? toNum(noLock.paidRate) : NaN;
  const lock = plan ? lockEpochs(plan) : null;
  const days = Number(period);
  const multiple = useMemo(() => planMultiple(r, r0, lock, days * 2), [r, r0, lock, days]);
  const points = useMemo(() => {
    const step = Math.max(1, Math.round(days / 45));
    const out: XY[] = [];
    for (let d = 0; d <= days; d += step) out.push({ x: d, y: 1000 * planMultiple(r, r0, lock, d * 2) });
    if (out[out.length - 1].x !== days) out.push({ x: days, y: 1000 * multiple });
    return out;
  }, [r, r0, lock, days, multiple]);
  const label = { '30': t('stake.d30'), '90': t('stake.d90'), '365': t('stake.d365') }[period];
  const ok = plan !== undefined && Number.isFinite(multiple);
  return (
    <Card className="stake-projection">
      <div className="card-head">
        <h3>{t('stake.projection')}</h3>
        <Segmented<'30' | '90' | '365'>
          label="period"
          value={period}
          onChange={setPeriod}
          options={[
            { value: '30', label: t('stake.d30') },
            { value: '90', label: t('stake.d90') },
            { value: '365', label: t('stake.d365') },
          ]}
        />
      </div>
      <div className="soft" style={{ fontSize: 13 }}>
        1,000 SYN · {plan ? t(planName(plan.id)) : '—'} · {t('stake.after', { period: label })}
      </div>
      <div className="figure-coin">
        <Coin token="SYN" size={40} />
        <div className="big" data-testid="stake-projection">
          <CountUp value={ok ? 1000 * multiple : undefined} format={(v) => v.toLocaleString('en-US', { maximumFractionDigits: 1 })} />{' '}
          <span className="gradient-text unit">SYN</span>
        </div>
      </div>
      <div className="pos" style={{ fontSize: 13, fontWeight: 700 }}>
        {ok ? `+${fmtPct(multiple - 1, 2)} · ${t('stake.compounding')}` : ' '}
      </div>
      <div style={{ marginTop: 8 }}>
        {ok ? (
          <AreaChart
            key={`${period}-${plan?.id}`}
            points={points}
            label={t('stake.projection')}
            formatX={(x) => t('dash2.day', { n: x })}
            formatY={(y) => fmtNum(y, 1)}
            height={180}
            arrow
          />
        ) : (
          <span className="skeleton" />
        )}
      </div>
      <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
        {lock !== null && days * 2 > lock ? t('stake.projectionSwitch', { days: Math.round(lock / 2) }) : t('stake.projectionNote')}
      </p>
    </Card>
  );
}
